import React, {
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import styles from "./CustomSelect.module.css";

/* ── Shared types ── */
export interface SelectOption {
  value: string;
  label: string;
  [key: string]: any;
}

export interface GroupedOptions {
  label: string;
  options: SelectOption[];
}

/* ── Shared hook: close on outside click ── */
function useClickOutside(
  ref: React.RefObject<HTMLDivElement | null>,
  onClose: () => void,
) {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onClose]);
}

/* ── Shared hook: trap scroll inside dropdown ── */
function useScrollTrap(
  ref: React.RefObject<HTMLDivElement | null>,
  active: boolean,
) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !active) return;
    const handler = (e: WheelEvent) => {
      e.stopPropagation();
      e.preventDefault();
      el.scrollTop += e.deltaY;
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [ref, active]);
}

/* ════════════════════════════════════════════════════════════════
   CustomMultiSelect
   ════════════════════════════════════════════════════════════════ */
interface CustomMultiSelectProps {
  options: SelectOption[] | GroupedOptions[];
  selected: SelectOption[];
  onChange: (selected: SelectOption[]) => void;
  placeholder?: string;
  selectAllLabel?: string;
  allOptions?: SelectOption[];
}

function isGrouped(
  opts: SelectOption[] | GroupedOptions[],
): opts is GroupedOptions[] {
  return opts.length > 0 && "options" in opts[0];
}

function flattenOptions(
  opts: SelectOption[] | GroupedOptions[],
): SelectOption[] {
  if (isGrouped(opts)) return opts.flatMap((g) => g.options);
  return opts;
}

export const CustomMultiSelect: React.FC<CustomMultiSelectProps> = ({
  options,
  selected,
  onChange,
  placeholder = "Search...",
  selectAllLabel,
  allOptions,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(500);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const closeDropdown = useCallback((returnFocus = false) => {
    setIsOpen(false);
    setSearch("");
    setActiveIndex(-1);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  useClickOutside(
    containerRef,
    useCallback(() => closeDropdown(false), [closeDropdown]),
  );
  useScrollTrap(dropdownRef, isOpen);

  useEffect(() => {
    if (isOpen && searchRef.current) searchRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 24;

    setDropdownMaxHeight(Math.max(160, Math.min(480, spaceBelow)));
  }, [isOpen]);

  const selectedSet = useMemo(
    () => new Set(selected.map((s) => s.value)),
    [selected],
  );

  const allFlat = useMemo(
    () => allOptions ?? flattenOptions(options),
    [options, allOptions],
  );

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return allFlat.filter(
      (o) => !selectedSet.has(o.value) && o.label.toLowerCase().includes(term),
    );
  }, [allFlat, selectedSet, search]);

  const filteredGrouped = useMemo(() => {
    if (!isGrouped(options)) return null;
    const term = search.toLowerCase();
    return options
      .map((g) => ({
        ...g,
        options: g.options.filter(
          (o) =>
            !selectedSet.has(o.value) && o.label.toLowerCase().includes(term),
        ),
      }))
      .filter((g) => g.options.length > 0);
  }, [options, selectedSet, search]);

  const toggle = (opt: SelectOption) => {
    if (selectedSet.has(opt.value)) {
      onChange(selected.filter((s) => s.value !== opt.value));
    } else {
      onChange([...selected, opt]);
    }
  };

  const selectAll = () => onChange([...allFlat]);

  const showSelectAll = !!(
    selectAllLabel &&
    !search &&
    selected.length < allFlat.length
  );
  const totalItems = (showSelectAll ? 1 : 0) + filtered.length;

  const scrollToIndex = (index: number) => {
    const dropdown = dropdownRef.current;
    if (!dropdown || index < 0) return;
    const el = dropdown.querySelector(
      `[data-cs-index="${index}"]`,
    ) as HTMLElement | null;
    if (!el) return;
    const top = el.offsetTop - dropdown.offsetTop;
    if (top < dropdown.scrollTop) {
      dropdown.scrollTop = top;
    } else if (
      top + el.offsetHeight >
      dropdown.scrollTop + dropdown.clientHeight
    ) {
      dropdown.scrollTop = top + el.offsetHeight - dropdown.clientHeight;
    }
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen((prev) => !prev);
    } else if (e.key === "Escape" && isOpen) {
      e.preventDefault();
      closeDropdown(true);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();
      setActiveIndex((prev) => {
        const next = prev + 1 < totalItems ? prev + 1 : 0;
        scrollToIndex(next);
        return next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      setActiveIndex((prev) => {
        const next = prev - 1 >= 0 ? prev - 1 : totalItems - 1;
        scrollToIndex(next);
        return next;
      });
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      e.stopPropagation();
      if (showSelectAll && activeIndex === 0) {
        selectAll();
      } else {
        const idx = showSelectAll ? activeIndex - 1 : activeIndex;
        if (filtered[idx]) toggle(filtered[idx]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      closeDropdown(true);
    }
  };

  return (
    <div className={styles.csContainer} ref={containerRef}>
      <div
        ref={triggerRef}
        className={styles.csTrigger}
        tabIndex={0}
        role="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleTriggerKeyDown}
      >
        {selected.length === 0 ? (
          <span className={styles.csPlaceholder}>Select...</span>
        ) : (
          <span className={styles.csCount}>{selected.length} selected</span>
        )}
        <span className={styles.csArrow} aria-hidden="true">
          {isOpen ? "▲" : "▼"}
        </span>
      </div>

      {isOpen && (
        <div
          className={styles.csDropdown}
          ref={dropdownRef}
          style={{ maxHeight: dropdownMaxHeight, overflowY: "auto" }}
        >
          <div className={styles.csSearchWrap}>
            <input
              ref={searchRef}
              type="text"
              className={styles.csSearchInput}
              placeholder={placeholder}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setActiveIndex(-1);
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleSearchKeyDown}
              aria-label="Search options"
            />
          </div>

          {showSelectAll && (
            <div
              data-cs-index={0}
              className={`${styles.csSelectAll} ${activeIndex === 0 ? styles.csItemFocused : ""}`}
              onClick={selectAll}
              onMouseEnter={() => setActiveIndex(0)}
            >
              {selectAllLabel}
            </div>
          )}

          {selected.length > 0 && (
            <div className={styles.csSelectedSection}>
              <div className={styles.csSectionHeader}>
                Selected
                <button
                  type="button"
                  className={styles.csClearAll}
                  onClick={() => onChange([])}
                  aria-label="Clear all selections"
                >
                  Clear all
                </button>
              </div>
              <div className={styles.csChipGrid}>
                {selected.map((s) => (
                  <span key={s.value} className={styles.csChip}>
                    {s.label}
                    <button
                      type="button"
                      className={styles.csChipX}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle(s);
                      }}
                      aria-label={`Remove ${s.label}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className={styles.csAvailableSection}>
            {filtered.length > 0 && (
              <div className={styles.csSectionHeader}>
                Available
                <span className={styles.csBadge}>{filtered.length}</span>
              </div>
            )}
            {filtered.length === 0 ? (
              <div className={styles.csEmpty}>
                {search ? "No matches found" : "All items selected"}
              </div>
            ) : filteredGrouped ? (
              (() => {
                let idx = showSelectAll ? 1 : 0;
                return filteredGrouped.map((g) => (
                  <div key={g.label}>
                    <div className={styles.csGroupLabel}>{g.label}</div>
                    {g.options.map((o) => {
                      const cur = idx++;
                      return (
                        <div
                          key={o.value}
                          data-cs-index={cur}
                          className={`${styles.csAvailableItem} ${activeIndex === cur ? styles.csItemFocused : ""}`}
                          onClick={() => toggle(o)}
                          onMouseEnter={() => setActiveIndex(cur)}
                        >
                          <span>{o.label}</span>
                          <span className={styles.csAddIcon} aria-hidden="true">
                            +
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ));
              })()
            ) : (
              filtered.map((o, i) => {
                const cur = (showSelectAll ? 1 : 0) + i;
                return (
                  <div
                    key={o.value}
                    data-cs-index={cur}
                    className={`${styles.csAvailableItem} ${activeIndex === cur ? styles.csItemFocused : ""}`}
                    onClick={() => toggle(o)}
                    onMouseEnter={() => setActiveIndex(cur)}
                  >
                    <span>{o.label}</span>
                    <span className={styles.csAddIcon} aria-hidden="true">
                      +
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
   CustomSingleSelect
   ════════════════════════════════════════════════════════════════ */
interface CustomSingleSelectProps {
  options: SelectOption[];
  value: SelectOption | null;
  onChange: (option: SelectOption | null) => void;
  placeholder?: string;
  isClearable?: boolean;
}

export const CustomSingleSelect: React.FC<CustomSingleSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  isClearable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(320);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const closeDropdown = useCallback((returnFocus = false) => {
    setIsOpen(false);
    setSearch("");
    setActiveIndex(-1);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  useClickOutside(
    containerRef,
    useCallback(() => closeDropdown(false), [closeDropdown]),
  );
  useScrollTrap(dropdownRef, isOpen);

  useEffect(() => {
    if (isOpen && searchRef.current) searchRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 24;

    setDropdownMaxHeight(Math.max(160, Math.min(480, spaceBelow)));
  }, [isOpen]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(term));
  }, [options, search]);

  const scrollToIndex = (index: number) => {
    const dropdown = dropdownRef.current;
    if (!dropdown || index < 0) return;
    const el = dropdown.querySelector(
      `[data-cs-index="${index}"]`,
    ) as HTMLElement | null;
    if (!el) return;
    const top = el.offsetTop - dropdown.offsetTop;
    if (top < dropdown.scrollTop) {
      dropdown.scrollTop = top;
    } else if (
      top + el.offsetHeight >
      dropdown.scrollTop + dropdown.clientHeight
    ) {
      dropdown.scrollTop = top + el.offsetHeight - dropdown.clientHeight;
    }
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen((prev) => !prev);
    } else if (e.key === "Escape" && isOpen) {
      e.preventDefault();
      closeDropdown(true);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();
      setActiveIndex((prev) => {
        const next = prev + 1 < filtered.length ? prev + 1 : 0;
        scrollToIndex(next);
        return next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      setActiveIndex((prev) => {
        const next = prev - 1 >= 0 ? prev - 1 : filtered.length - 1;
        scrollToIndex(next);
        return next;
      });
    } else if (e.key === "Enter" && activeIndex >= 0 && filtered[activeIndex]) {
      e.preventDefault();
      e.stopPropagation();
      onChange(filtered[activeIndex]);
      closeDropdown(false);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      closeDropdown(true);
    }
  };

  return (
    <div className={styles.csContainer} ref={containerRef}>
      <div
        ref={triggerRef}
        className={styles.csTrigger}
        tabIndex={0}
        role="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleTriggerKeyDown}
      >
        {value ? (
          <span className={styles.csSingleValue}>
            {value.label}
            {isClearable && (
              <button
                type="button"
                className={styles.csSingleClear}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                }}
                aria-label="Clear selection"
              >
                ×
              </button>
            )}
          </span>
        ) : (
          <span className={styles.csPlaceholder}>{placeholder}</span>
        )}
        <span className={styles.csArrow} aria-hidden="true">
          {isOpen ? "▲" : "▼"}
        </span>
      </div>

      {isOpen && (
        <div
          className={styles.csDropdown}
          ref={dropdownRef}
          style={{ maxHeight: dropdownMaxHeight, overflowY: "auto" }}
        >
          <div className={styles.csSearchWrap}>
            <input
              ref={searchRef}
              type="text"
              className={styles.csSearchInput}
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setActiveIndex(-1);
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleSearchKeyDown}
              aria-label="Search options"
            />
          </div>

          <div className={styles.csAvailableSection}>
            {filtered.length === 0 ? (
              <div className={styles.csEmpty}>No matches found</div>
            ) : (
              filtered.map((o, i) => (
                <div
                  key={o.value}
                  data-cs-index={i}
                  className={`${styles.csAvailableItem} ${
                    value?.value === o.value ? styles.csItemActive : ""
                  } ${activeIndex === i ? styles.csItemFocused : ""}`}
                  onClick={() => {
                    onChange(o);
                    closeDropdown(false);
                  }}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  <span>{o.label}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
